import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { BotReferenceMode } from '@core/interfaces/TBot';
import { TEmbeddingExternalReference, TSourceDetail } from '@core/interfaces/TMessage';
import { environment } from '@env/environment';

import { MetadataService, MetadataSlugs } from './metadata.service';

export type ReferenceCitationDisplayMode = BotReferenceMode;

const DEFAULT_REFERENCE_DISPLAY_MODE: ReferenceCitationDisplayMode = 'all';

function normalizeReferenceDisplayMode(value: string | null | undefined): ReferenceCitationDisplayMode {
  switch (value) {
    case 'source':
    case 'source_only':
      return 'source_only';
    case 'external':
    case 'ref_in_ref_only':
      return 'ref_in_ref_only';
    case 'all':
      return 'all';
    default:
      return DEFAULT_REFERENCE_DISPLAY_MODE;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ReferenceDisplayPreferencesService {
  private readonly _mode = signal<ReferenceCitationDisplayMode>(DEFAULT_REFERENCE_DISPLAY_MODE);
  private _loaded = false;

  readonly mode = this._mode.asReadonly();

  constructor(private _metadataService: MetadataService) {}

  setPersistedMode(mode: ReferenceCitationDisplayMode): void {
    this._mode.set(normalizeReferenceDisplayMode(mode));
    this._loaded = true;
  }

  ensureLoaded(): Observable<void> {
    if (!environment.referenceDisplayModeSettingEnabled) {
      this._mode.set(DEFAULT_REFERENCE_DISPLAY_MODE);
      return of(void 0);
    }
    if (this._loaded) {
      return of(void 0);
    }
    return this._metadataService.get<string>(MetadataSlugs.ReferenceCitationDisplayMode).pipe(
      tap(meta => {
        this._mode.set(normalizeReferenceDisplayMode(meta?.value));
      }),
      tap(() => {
        this._loaded = true;
      }),
      map(() => void 0),
      take(1),
      catchError(() => {
        this._mode.set(DEFAULT_REFERENCE_DISPLAY_MODE);
        this._loaded = true;
        return of(void 0);
      })
    );
  }

  referencePanelLabel(source: TSourceDetail): string {
    return source.citation || source.name;
  }

  shouldShowSourceReference(): boolean {
    return this._mode() !== 'ref_in_ref_only';
  }

  shouldShowReferenceInReference(): boolean {
    return this._mode() !== 'source_only';
  }

  shouldShowReferenceInReferenceRows(hasResolvedExternalReferences: boolean): boolean {
    return this.shouldShowReferenceInReference() && hasResolvedExternalReferences;
  }

  formatExternalReferenceLine(r: TEmbeddingExternalReference): string {
    const n = r.originalReferenceNumber != null ? `[${r.originalReferenceNumber}] ` : '';
    return `${n}${this.cleanExternalReferenceText(r.referenceText)}`.trim();
  }

  dedupeExternalReferences(
    refs: TEmbeddingExternalReference[] | undefined | null
  ): TEmbeddingExternalReference[] {
    if (!refs?.length) {
      return [];
    }

    const deduped = new Map<string, TEmbeddingExternalReference>();
    refs.forEach(ref => {
      const cleanedText = this.cleanExternalReferenceText(ref.referenceText);
      const key = cleanedText.toLowerCase();
      const normalizedUrl = this.externalReferenceUrl(ref);
      const existing = deduped.get(key);

      if (!existing) {
        deduped.set(key, {
          ...ref,
          referenceText: cleanedText,
          referenceLink: normalizedUrl ?? ref.referenceLink,
        });
        return;
      }

      if (!this.externalReferenceUrl(existing) && normalizedUrl) {
        existing.referenceLink = normalizedUrl;
      }
    });

    return Array.from(deduped.values());
  }

  externalReferenceUrl(ref: TEmbeddingExternalReference | undefined | null): string | null {
    const explicitUrl = ref?.referenceLink?.trim();
    if (explicitUrl) {
      return explicitUrl;
    }

    const doi = ref?.referenceText.match(/(?:doi:\s*)?(10\.\d{4,9}\/[^\s;]+)/i)?.[1];
    return doi ? `https://doi.org/${doi}` : null;
  }

  private cleanExternalReferenceText(text: string | undefined): string {
    return (text || '').replace(/\s+/g, ' ').trim();
  }
}
